<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <button type="button" class="profile-dropdown__trigger">
          <span class="profile-dropdown__avatar">{{ userInitials }}</span>
          <span class="profile-dropdown__name hidden md:inline">{{ userDisplayName }}</span>
          <VaIcon name="mso-expand_more" size="16px" class="profile-dropdown__chevron" />
        </button>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-64 px-0 py-3 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <div class="profile-dropdown__header">
          <span class="profile-dropdown__avatar profile-dropdown__avatar--lg">{{ userInitials }}</span>
          <div class="min-w-0">
            <p class="profile-dropdown__header-name">{{ userDisplayName }}</p>
            <p class="profile-dropdown__header-email">{{ authStore.user?.email || '' }}</p>
          </div>
        </div>

        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from '@/stores/auth'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const userDisplayName = computed(() => {
  const user = authStore.user
  if (!user) return 'Utilisateur'
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return fullName || user.username || 'Utilisateur'
})

const userInitials = computed(() => {
  const name = userDisplayName.value
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return name.slice(0, 2).toUpperCase()
})

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          // {
          //   name: 'settings',
          //   to: 'settings',
          //   icon: 'mso-settings',
          // },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const handleItemClick = async (item: ProfileListItem) => {
  if (item.name === 'logout') {
    authStore.logout()
    router.push({ name: 'login' })
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout') {
    return {}
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss" scoped>
.profile-dropdown-wrapper {
  :deep(.va-dropdown__anchor) {
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}

.profile-dropdown {
  cursor: pointer;

  &__trigger {
    display: inline-flex;
    align-items: center;

    gap: 0.5rem;

    padding: 0.3rem 0.5rem 0.3rem 0.3rem;

    border: 0;
    border-radius: 999px;

    background: #ffffff;

    cursor: pointer;

    transition:
      background-color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      background: rgba(var(--va-primary-rgb, 15, 34, 204), 0.08);
      transform: translateY(-1px);
    }

    &:active {
      transform: scale(0.97);
    }
  }

  &__avatar {
    width: 30px;
    height: 30px;

    flex-shrink: 0;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    border-radius: 50%;

    background: linear-gradient(135deg, var(--va-primary), rgba(var(--va-primary-rgb, 15, 34, 204), 0.75));
    color: #ffffff;

    font-size: 0.72rem;
    font-weight: 800;

    box-shadow: 0 3px 8px rgba(var(--va-primary-rgb, 15, 34, 204), 0.28);

    &--lg {
      width: 42px;
      height: 42px;

      font-size: 0.9rem;
    }
  }

  &__name {
    color: #0f172a;
    font-size: 0.82rem;
    font-weight: 700;

    white-space: nowrap;
  }

  &__chevron {
    color: #94a3b8;
  }

  &__header {
    display: flex;
    align-items: center;

    gap: 0.65rem;

    padding: 0.4rem 1rem 0.9rem;

    margin-bottom: 0.4rem;

    border-bottom: 1px solid rgba(15, 23, 42, 0.07);
  }

  &__header-name {
    margin: 0;

    color: #0f172a;
    font-size: 0.88rem;
    font-weight: 800;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__header-email {
    margin: 0.1rem 0 0;

    color: #94a3b8;
    font-size: 0.72rem;
    font-weight: 500;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__content {
    background: var(--va-background-secondary);
    border-radius: 0.75rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--va-background-primary);
    animation: dropdown-enter 0.2s ease;

    @keyframes dropdown-enter {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    header {
      padding: 0.75rem 1rem;
      background: linear-gradient(135deg, rgba(15, 34, 204, 0.05) 0%, rgba(79, 70, 229, 0.03) 100%);
      border-bottom: 1px solid rgba(15, 34, 204, 0.1);
      margin-bottom: 0.5rem;
      font-size: 0.7rem;
      letter-spacing: 0.05em;
    }

    .menu-item {
      transition: all 0.2s ease;
      border-radius: 0.375rem;
      margin: 0.25rem 0.5rem;

      &:hover {
        background: linear-gradient(135deg, rgba(15, 34, 204, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%);
        transform: translateX(4px);
        color: var(--va-primary);

        :deep(.va-icon) {
          color: var(--va-primary) !important;
        }
      }

      :deep(.va-icon) {
        transition: all 0.2s ease;
      }
    }
  }
}
</style>
